import React, { useMemo, useState } from 'react'
import { Autocomplete, Box, TextField } from '@mui/material'
import debounce from 'lodash/debounce'
import Dialog from 'components/dialog/dialog'
import Notification from 'components/notification'
import type { HyundaiCatalogListProps } from 'modules/types'
import {
  useBulkUpdateHyundaiCatalogImageMutation,
  useGetHyundaiCatalogListMutation
} from 'store/hyundaiModel'

type BulkImageUploadProps = {
  open: boolean
  carModel: string
  onClose: () => void
}

export default function BulkImageUpload({ open, carModel, onClose }: BulkImageUploadProps) {
  const [file, setFile] = useState<File | null>(null)
  const [previewImage, setPreviewImage] = useState<string>('')
  const [selectedTrims, setSelectedTrims] = useState<HyundaiCatalogListProps[]>([])
  const [searchInput, setSearchInput] = useState<string>('')
  const [bulkUpdateHyundaiCatalogImage, bulkUpdate] = useBulkUpdateHyundaiCatalogImageMutation()
  const [getHyundaiCatalogList, catalogSearch] = useGetHyundaiCatalogListMutation()

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length < 3 && value.length > 0) return
        const formData = new FormData()
        formData.append('start', '0')
        formData.append('length', '25')
        formData.append('field', 'name,trim,year,color')
        formData.append('car-model', carModel)
        if (value) formData.append('search[value]', value)
        getHyundaiCatalogList(formData)
      }, 400),
    [carModel]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      const reader = new FileReader()
      reader.onload = () => setPreviewImage(reader.result as string)
      reader.readAsDataURL(files[0])
    }
  }

  const handleSubmit = async () => {
    if (!file || selectedTrims.length === 0) return

    const formData = new FormData()
    formData.append('image', file)
    selectedTrims.forEach((t) => formData.append('trimIds', String(t.id)))

    bulkUpdateHyundaiCatalogImage(formData)
      .unwrap()
      .then(() => {
        onClose()
        window.location.reload()
      })
      .catch(() => {
        // Error handled by Notification component
      })
  }

  const catalogOptions = (catalogSearch.data?.data as HyundaiCatalogListProps[]) || []

  return (
    <>
      <Dialog
        open={open}
        title='Assign Image to Trims'
        maxWidth='xl'
        onCancel={onClose}
        onSubmit={handleSubmit}
        loading={bulkUpdate.isLoading}
        isDisabled={!file || selectedTrims.length === 0}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          {/* File picker + preview */}
          <Box>
            <TextField
              fullWidth
              type='file'
              id='carImage'
              label='Car Image'
              inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
              onChange={handleFileChange}
              InputLabelProps={{ shrink: true }}
            />
            {previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={previewImage}
                  alt='Preview'
                  style={{ maxWidth: '100%', maxHeight: '200px' }}
                />
              </Box>
            )}
          </Box>

          {/* Searchable multi-select */}
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect
            clearOnBlur={false}
            blurOnSelect={false}
            id='trim-autocomplete'
            options={catalogOptions}
            value={selectedTrims}
            inputValue={searchInput}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setSearchInput(value)
                if (value.length > 3) debouncedSearch(value)
              }
            }}
            onChange={(_, newValue) => {
              setSelectedTrims(newValue)
            }}
            getOptionLabel={(option) => `${option.trim} ${option.year} ${option.colorCode}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={catalogSearch.isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Select Trims'
                placeholder='Search trims (type 3+ chars)...'
              />
            )}
            noOptionsText='No trims found'
          />
        </Box>
      </Dialog>

      {bulkUpdate.error && (
        <Notification
          open={!!bulkUpdate.error}
          onClose={undefined}
          message={
            (((bulkUpdate.error as Record<string, unknown>)?.data as Record<string, unknown>)
              ?.message as string) || 'Failed to upload image. Please try again.'
          }
          isError
        />
      )}
    </>
  )
}
